/**
 * Alert feed (PRD §5.8 S-03).
 *
 * Compact, scrollable list of recent incidents — newest first. Used in
 * the right column of the Ops Center. Pure presentational: takes
 * `incidents[]` and emits `onIncidentClick(id)` callbacks.
 *
 * Visual rules:
 *   - DRAFT rows pulse with an amber accent
 *   - ACTIVE / ACKNOWLEDGED / INVESTIGATING with rose accent
 *   - RESOLVED with muted slate
 *   - selected row has a teal ring
 */

import { Link } from "wouter";
import { StatusBadge } from "./StatusBadge";
import type { IncidentDto } from "@/lib/types";

export interface AlertFeedProps {
  incidents: IncidentDto[];
  /** Callback when a row is clicked. If absent, rows render as <Link> to
   *  /incidents/:id (legacy Dashboard behaviour). */
  onIncidentClick?: (incidentId: string) => void;
  /** Currently-selected incident — drawn with a ring. */
  selectedIncidentId?: string | null;
  /** Whether the list is currently loading. */
  loading?: boolean;
  /** Show this many incidents max (default: all). */
  maxRows?: number;
}

export function AlertFeed({
  incidents,
  onIncidentClick,
  selectedIncidentId,
  loading,
  maxRows,
}: AlertFeedProps) {
  const visible = maxRows ? incidents.slice(0, maxRows) : incidents;
  return (
    <section
      className="rounded-lg border border-slate-800 bg-slate-900/40 flex flex-col"
      data-testid="alert-feed"
    >
      <header className="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-semibold">
          Alerts
        </span>
        <span className="text-[10px] text-slate-500 font-mono">
          {incidents.length} total
        </span>
      </header>

      {loading && (
        <div
          className="p-4 text-xs text-slate-500"
          data-testid="alert-feed-loading"
        >
          Loading…
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div
          className="p-4 text-xs text-slate-500 italic"
          data-testid="alert-feed-empty"
        >
          No incidents in the last 24h.
        </div>
      )}

      {!loading && visible.length > 0 && (
        <ul
          className="divide-y divide-slate-800 overflow-y-auto"
          data-testid="alert-feed-list"
          style={{ maxHeight: 480 }}
        >
          {visible.map((incident) => {
            const isSelected = selectedIncidentId === incident.id;
            const isPulsing = incident.status === "DRAFT";
            const accent = pickAccent(incident.status);
            const inner = (
              <div
                className={
                  `block px-3 py-2 transition group ` +
                  (isSelected
                    ? "bg-teal-500/15 ring-1 ring-inset ring-teal-500/40"
                    : "hover:bg-slate-800/40")
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusBadge status={incident.status} />
                    <span
                      className={`text-xs font-mono truncate ${accent} ${isPulsing ? "animate-pulse" : ""}`}
                    >
                      {incident.eventType}
                    </span>
                  </div>
                  <span
                    className={`text-[10px] font-mono ${
                      incident.sla.colour === "red"
                        ? "text-rose-400"
                        : incident.sla.colour === "amber"
                        ? "text-amber-300"
                        : "text-slate-500"
                    }`}
                  >
                    {incident.sla.elapsedSeconds}s
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">
                  {incident.segmentId} ·{" "}
                  {Math.round(incident.confidence * 100)}%
                </div>
              </div>
            );
            return (
              <li
                key={incident.id}
                data-testid={`alert-feed-row-${incident.id}`}
                data-incident-status={incident.status}
              >
                {onIncidentClick ? (
                  <button
                    type="button"
                    onClick={() => onIncidentClick(incident.id)}
                    className="w-full text-left"
                  >
                    {inner}
                  </button>
                ) : (
                  <Link href={`/incidents/${incident.id}`} className="block">
                    {inner}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function pickAccent(status: IncidentDto["status"]): string {
  switch (status) {
    case "DRAFT":
      return "text-amber-200";
    case "ACTIVE":
    case "ACKNOWLEDGED":
    case "INVESTIGATING":
      return "text-rose-200";
    case "RESOLVED":
      return "text-slate-400";
    default:
      return "text-slate-200";
  }
}
